import React, { useEffect, useState } from 'react';
import {
  FiActivity,
  FiBarChart2,
  FiDownload,
  FiEye,
  FiGlobe,
  FiMonitor,
  FiRefreshCw,
  FiSmartphone,
  FiTablet,
  FiTrash2,
  FiTrendingDown,
  FiTrendingUp,
  FiUsers,
  FiX,
} from 'react-icons/fi';
import {
  Area,
  AreaChart,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import apiService from '../services/api';

const Analytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('last30days');
  const [realTimeData, setRealTimeData] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    fetchAnalytics();
    fetchRealTimeData();
    // Set up real-time data refresh every 30 seconds
    const interval = setInterval(fetchRealTimeData, 30000);
    return () => clearInterval(interval);
  }, [timeRange]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await apiService.getDetailedAnalytics(timeRange);
      setAnalytics(response.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchRealTimeData = async () => {
    try {
      const response = await apiService.getRealtimeAnalytics();
      setRealTimeData(response.data);
    } catch (err) {
      console.error('Error fetching real-time data:', err);
    }
  };

  const handleExport = async (format) => {
    try {
      setExportLoading(true);
      await apiService.exportAnalytics(format);
      setSuccessMessage(`Analytics exported as ${format.toUpperCase()} successfully!`);
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      setError(`Export failed: ${err.message}`);
    } finally {
      setExportLoading(false);
    }
  };

  const handleReset = async () => {
    try {
      setResetLoading(true);
      const response = await apiService.resetAnalytics();
      setSuccessMessage(response.message);
      setShowResetConfirm(false);
      
      // Refresh analytics data
      await fetchAnalytics();
      await fetchRealTimeData();
      
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      setError(`Reset failed: ${err.message}`);
    } finally {
      setResetLoading(false);
    }
  };

  const handleCleanup = async () => {
    try {
      setResetLoading(true);
      const response = await apiService.cleanupOldAnalytics();
      setSuccessMessage(response.message);
      
      // Refresh analytics data
      await fetchAnalytics();
      await fetchRealTimeData();
      
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      setError(`Cleanup failed: ${err.message}`);
    } finally {
      setResetLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toString() || '0';
  };

  const getDeviceIcon = (device) => {
    switch (device) {
      case 'mobile': return FiSmartphone;
      case 'tablet': return FiTablet;
      default: return FiMonitor;
    }
  };

  const calculatePercentageChange = (current, previous) => {
    if (!previous || previous === 0) return 0;
    return ((current - previous) / previous * 100).toFixed(1);
  };

  if (loading && !analytics) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-gray-900 to-black flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <span className="text-white text-lg">Loading analytics...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-gray-900 to-black p-6">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-800/80 via-gray-800/80 to-slate-800/80 backdrop-blur-xl rounded-3xl p-8 border border-white/10 shadow-2xl">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-white via-cyan-200 to-purple-200 bg-clip-text text-transparent font-display mb-2">
                Website Analytics
              </h1>
              <p className="text-slate-300 text-lg">
                Monitor website performance and user behavior
              </p>
            </div>
            <div className="flex items-center gap-4">
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                className="px-4 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-400/30"
              >
                <option value="last7days">Last 7 Days</option>
                <option value="last30days">Last 30 Days</option>
                <option value="last90days">Last 90 Days</option>
                <option value="last12months">Last 12 Months</option>
              </select>
              <button
                onClick={() => {
                  fetchAnalytics();
                  fetchRealTimeData();
                }}
                className="px-4 py-2 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-400/30 rounded-lg text-purple-400 text-sm transition-all duration-300 flex items-center gap-2 hover:scale-105"
              >
                <FiRefreshCw className="w-4 h-4" />
                Refresh
              </button>
            </div>
          </div>

          {/* Success Message */}
          {successMessage && (
            <div className="mt-4 p-4 bg-green-500/20 border border-green-400/30 rounded-lg flex items-center justify-between text-green-400">
              <span>{successMessage}</span>
              <button onClick={() => setSuccessMessage('')}>
                <FiX className="w-4 h-4" />
              </button>
            </div>
          )}

          {error && (
            <div className="mt-4 p-4 bg-red-500/20 border border-red-400/30 rounded-lg flex items-center justify-between text-red-400">
              <span>{error}</span>
              <button onClick={() => setError(null)}>
                <FiX className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Data Management Section */}
          <div className="mt-6 border-t border-slate-700/50 pt-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-white mb-1">Data Management</h3>
                <p className="text-slate-400 text-sm">Export data or clean up old records to optimize storage</p>
              </div>
              <div className="flex items-center gap-3">
                {/* Export Buttons */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleExport('json')}
                    disabled={exportLoading}
                    className="px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-400/30 rounded-lg text-blue-400 text-sm transition-all duration-300 flex items-center gap-2 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FiDownload className="w-4 h-4" />
                    {exportLoading ? 'Exporting...' : 'Export JSON'}
                  </button>
                  <button
                    onClick={() => handleExport('csv')}
                    disabled={exportLoading}
                    className="px-4 py-2 bg-green-500/20 hover:bg-green-500/30 border border-green-400/30 rounded-lg text-green-400 text-sm transition-all duration-300 flex items-center gap-2 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FiDownload className="w-4 h-4" />
                    {exportLoading ? 'Exporting...' : 'Export CSV'}
                  </button>
                </div>

                {/* Cleanup & Reset Buttons */}
                <div className="flex items-center gap-2 border-l border-slate-700/50 pl-3">
                  <button
                    onClick={handleCleanup}
                    disabled={resetLoading}
                    className="px-4 py-2 bg-orange-500/20 hover:bg-orange-500/30 border border-orange-400/30 rounded-lg text-orange-400 text-sm transition-all duration-300 flex items-center gap-2 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FiTrash2 className="w-4 h-4" />
                    {resetLoading ? 'Cleaning...' : 'Cleanup Old Data'}
                  </button>
                  <button
                    onClick={() => setShowResetConfirm(true)}
                    disabled={resetLoading}
                    className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 border border-red-400/30 rounded-lg text-red-400 text-sm transition-all duration-300 flex items-center gap-2 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FiTrash2 className="w-4 h-4" />
                    {resetLoading ? 'Resetting...' : 'Reset All Data'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Reset Confirmation Modal */}
        {showResetConfirm && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-gradient-to-br from-slate-800 via-gray-800 to-slate-800 rounded-2xl p-8 border border-red-400/30 shadow-2xl max-w-md w-full mx-4">
              <div className="text-center">
                <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FiTrash2 className="w-8 h-8 text-red-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Reset All Analytics Data</h3>
                <p className="text-slate-300 mb-6">
                  This action will permanently delete all analytics data from the database. 
                  <br />
                  <strong className="text-red-400">This cannot be undone!</strong>
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowResetConfirm(false)}
                    className="flex-1 px-4 py-2 bg-slate-600/50 hover:bg-slate-600/70 rounded-lg text-white transition-all duration-300"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleReset}
                    disabled={resetLoading}
                    className="flex-1 px-4 py-2 bg-red-500 hover:bg-red-600 rounded-lg text-white transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {resetLoading ? 'Resetting...' : 'Reset All Data'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {analytics && (
          <>
            {/* Real-time Stats */}
            {realTimeData && (
              <div className="bg-gradient-to-br from-slate-800/80 via-gray-800/80 to-slate-800/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-2xl">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-white">Real-time Activity</h2>
                  <div className="flex items-center gap-2 text-green-400">
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                    <span className="text-sm">Live</span>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-white mb-2">{realTimeData.activeUsers || 0}</div>
                    <p className="text-slate-300 text-sm">Active Users</p>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-white mb-2">{realTimeData.pageViews || 0}</div>
                    <p className="text-slate-300 text-sm">Page Views (Last Hour)</p>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-white mb-2">{realTimeData.sessions || 0}</div>
                    <p className="text-slate-300 text-sm">Active Sessions</p>
                  </div>
                </div>
              </div>
            )}

            {/* Main Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-gradient-to-br from-slate-800/80 via-gray-800/80 to-slate-800/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-2xl">
                <div className="flex items-center justify-between mb-4">
                  <FiUsers className="w-8 h-8 text-cyan-400" />
                  <div className="text-right">
                    <div className="text-2xl font-bold text-white">{formatNumber(analytics.visitors?.total || 0)}</div>
                    <div className="flex items-center gap-1 text-sm">
                      {analytics.visitors?.growth >= 0 ? (
                        <FiTrendingUp className="w-3 h-3 text-green-400" />
                      ) : (
                        <FiTrendingDown className="w-3 h-3 text-red-400" />
                      )}
                      <span className={analytics.visitors?.growth >= 0 ? 'text-green-400' : 'text-red-400'}>
                        {Math.abs(Number(analytics.visitors?.growth || 0)).toFixed(1)}%
                      </span>
                    </div>
                  </div>
                </div>
                <h3 className="text-slate-300 text-sm font-medium">Total Visitors</h3>
              </div>

              <div className="bg-gradient-to-br from-slate-800/80 via-gray-800/80 to-slate-800/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-2xl">
                <div className="flex items-center justify-between mb-4">
                  <FiEye className="w-8 h-8 text-blue-400" />
                  <div className="text-right">
                    <div className="text-2xl font-bold text-white">{formatNumber(analytics.pageViews?.total || 0)}</div>
                    <div className="flex items-center gap-1 text-sm">
                      {analytics.pageViews?.growth >= 0 ? (
                        <FiTrendingUp className="w-3 h-3 text-green-400" />
                      ) : (
                        <FiTrendingDown className="w-3 h-3 text-red-400" />
                      )}
                      <span className={analytics.pageViews?.growth >= 0 ? 'text-green-400' : 'text-red-400'}>
                        {Math.abs(Number(analytics.pageViews?.growth || 0)).toFixed(1)}%
                      </span>
                    </div>
                  </div>
                </div>
                <h3 className="text-slate-300 text-sm font-medium">Page Views</h3>
              </div>

              <div className="bg-gradient-to-br from-slate-800/80 via-gray-800/80 to-slate-800/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-2xl">
                <div className="flex items-center justify-between mb-4">
                  <FiActivity className="w-8 h-8 text-green-400" />
                  <div className="text-right">
                    <div className="text-2xl font-bold text-white">{formatNumber(analytics.sessions?.total || 0)}</div>
                    <div className="flex items-center gap-1 text-sm">
                      {analytics.sessions?.growth >= 0 ? (
                        <FiTrendingUp className="w-3 h-3 text-green-400" />
                      ) : (
                        <FiTrendingDown className="w-3 h-3 text-red-400" />
                      )}
                      <span className={analytics.sessions?.growth >= 0 ? 'text-green-400' : 'text-red-400'}>
                        {Math.abs(Number(analytics.sessions?.growth || 0)).toFixed(1)}%
                      </span>
                    </div>
                  </div>
                </div>
                <h3 className="text-slate-300 text-sm font-medium">Sessions</h3>
              </div>

              <div className="bg-gradient-to-br from-slate-800/80 via-gray-800/80 to-slate-800/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-2xl">
                <div className="flex items-center justify-between mb-4">
                  <FiBarChart2 className="w-8 h-8 text-purple-400" />
                  <div className="text-right">
                    <div className="text-2xl font-bold text-white">{Number(analytics.bounceRate || 0).toFixed(1)}%</div>
                    <div className="flex items-center gap-1 text-sm">
                      {analytics.bounceRateChange <= 0 ? (
                        <FiTrendingUp className="w-3 h-3 text-green-400" />
                      ) : (
                        <FiTrendingDown className="w-3 h-3 text-red-400" />
                      )}
                      <span className={analytics.bounceRateChange <= 0 ? 'text-green-400' : 'text-red-400'}>
                        {Math.abs(Number(analytics.bounceRateChange || 0)).toFixed(1)}%
                      </span>
                    </div>
                  </div>
                </div>
                <h3 className="text-slate-300 text-sm font-medium">Bounce Rate</h3>
              </div>
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Traffic Chart */}
              <div className="bg-gradient-to-br from-slate-800/80 via-gray-800/80 to-slate-800/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-2xl">
                <h3 className="text-xl font-bold text-white mb-6">Traffic Overview</h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={analytics.dailyData || []}>
                      <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
                      <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#1e293b',
                          border: '1px solid #334155',
                          borderRadius: '8px',
                          color: '#ffffff'
                        }}
                      />
                      <Area
                        type="monotone"
                        dataKey="visitors"
                        stroke="#06b6d4"
                        fill="url(#visitorGradient)"
                        strokeWidth={2}
                      />
                      <defs>
                        <linearGradient id="visitorGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#06b6d4" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Device Breakdown */}
              <div className="bg-gradient-to-br from-slate-800/80 via-gray-800/80 to-slate-800/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-2xl">
                <h3 className="text-xl font-bold text-white mb-6">Device Breakdown</h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={analytics.deviceBreakdown || []}
                        dataKey="value"
                        nameKey="device"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        fill="#8884d8"
                        label={({ device, percent }) => `${device} ${(percent * 100).toFixed(0)}%`}
                      >
                        {(analytics.deviceBreakdown || []).map((entry, index) => {
                          const colors = ['#06b6d4', '#8b5cf6', '#10b981', '#f59e0b'];
                          return <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />;
                        })}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#1e293b',
                          border: '1px solid #334155',
                          borderRadius: '8px',
                          color: '#ffffff'
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* Top Pages & Traffic Sources */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Top Pages */}
              <div className="bg-gradient-to-br from-slate-800/80 via-gray-800/80 to-slate-800/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-2xl">
                <h3 className="text-xl font-bold text-white mb-6">Top Pages</h3>
                <div className="space-y-4">
                  {(analytics.topPages || []).slice(0, 5).map((page, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium truncate">{page.path}</p>
                        <p className="text-slate-400 text-sm">{page.views} views</p>
                      </div>
                      <div className="ml-4 text-right">
                        <div className="w-16 bg-slate-600 rounded-full h-2">
                          <div 
                            className="bg-cyan-400 h-2 rounded-full" 
                            style={{ 
                              width: `${(page.views / Math.max(...(analytics.topPages || []).map(p => p.views))) * 100}%` 
                            }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Traffic Sources */}
              <div className="bg-gradient-to-br from-slate-800/80 via-gray-800/80 to-slate-800/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-2xl">
                <h3 className="text-xl font-bold text-white mb-6">Traffic Sources</h3>
                <div className="space-y-4">
                  {(analytics.trafficSources || []).slice(0, 5).map((source, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center">
                          <FiGlobe className="w-4 h-4 text-blue-400" />
                        </div>
                        <div>
                          <p className="text-white font-medium">{source.source}</p>
                          <p className="text-slate-400 text-sm">{source.visitors} visitors</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-white font-medium">{source.percentage}%</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Performance Metrics */}
            {analytics.performance && (
              <div className="bg-gradient-to-br from-slate-800/80 via-gray-800/80 to-slate-800/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-2xl">
                <h3 className="text-xl font-bold text-white mb-6">Performance Metrics</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="text-center p-4 bg-slate-700/30 rounded-lg">
                    <div className="text-2xl font-bold text-white mb-2">
                      {Number(analytics.performance.avgLoadTime || 0).toFixed(2)}s
                    </div>
                    <p className="text-slate-300 text-sm">Avg Load Time</p>
                  </div>
                  <div className="text-center p-4 bg-slate-700/30 rounded-lg">
                    <div className="text-2xl font-bold text-white mb-2">
                      {Number(analytics.performance.avgSessionDuration || 0).toFixed(1)}min
                    </div>
                    <p className="text-slate-300 text-sm">Avg Session Duration</p>
                  </div>
                  <div className="text-center p-4 bg-slate-700/30 rounded-lg">
                    <div className="text-2xl font-bold text-white mb-2">
                      {Number(analytics.performance.pagesPerSession || 0).toFixed(1)}
                    </div>
                    <p className="text-slate-300 text-sm">Pages per Session</p>
                  </div>
                  <div className="text-center p-4 bg-slate-700/30 rounded-lg">
                    <div className="text-2xl font-bold text-white mb-2">
                      {Number(analytics.performance.conversionRate || 0).toFixed(2)}%
                    </div>
                    <p className="text-slate-300 text-sm">Conversion Rate</p>
                  </div>
                </div>
              </div>
            )}

            {/* Geographic Data */}
            {analytics.geography && (
              <div className="bg-gradient-to-br from-slate-800/80 via-gray-800/80 to-slate-800/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-2xl">
                <h3 className="text-xl font-bold text-white mb-6">Geographic Distribution</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-4">Top Countries</h4>
                    <div className="space-y-3">
                      {(analytics.geography.countries || []).slice(0, 5).map((country, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
                          <span className="text-white">{country.name}</span>
                          <span className="text-slate-300">{country.visitors} visitors</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-4">Top Cities</h4>
                    <div className="space-y-3">
                      {(analytics.geography.cities || []).slice(0, 5).map((city, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
                          <span className="text-white">{city.name}</span>
                          <span className="text-slate-300">{city.visitors} visitors</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Analytics; 